import React from "react";
import { SEO } from "./SEO";
import { CategoryData } from "../../types-var/product";

interface CategorySEOProps {
  category: CategoryData;
}

// Helper function to treat null/undefined/empty string as missing
const safeValue = (value: any): any => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return value;
};

export const CategorySEO: React.FC<CategorySEOProps> = ({ category }) => {
  // Debug logging
  console.log("🔍 CategorySEO received category:", category);
  console.log("🔍 CategorySEO received SEO data:", category.seo);

  // Safely access breadcrumbs with fallback to empty array
  const breadcrumbs = category.breadcrumbs || [];

  // Get SEO data from category if available
  const seoData = category.seo || null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${typeof window !== "undefined" ? window.location.origin : ""}${
        item.url
      }`,
    })),
  };

  // Create a description from breadcrumbs and SEO data
  const fallbackDescription = `Explora nuestra selección de ${safeValue(
    category.name
  )}${
    breadcrumbs.length > 1
      ? ` en ${breadcrumbs
          .slice(0, -1)
          .map((b) => safeValue(b.name))
          .join(" > ")}`
      : ""
  }`;

  // Create keywords from breadcrumbs and category name
  const fallbackKeywords = [
    ...breadcrumbs.map((b) => safeValue(b.name)),
    safeValue(category.name),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <SEO
      title={safeValue(seoData?.metaTitle) || safeValue(category.name)}
      description={safeValue(seoData?.metaDescription) || fallbackDescription}
      image={safeValue(seoData?.metaImage?.url)}
      type="website"
      schema={breadcrumbSchema}
      seoData={seoData}
      openGraph={seoData?.openGraph}
      useGlobalFallbacks={true}
    >
      <meta
        name="keywords"
        content={safeValue(seoData?.keywords) || fallbackKeywords}
      />
      {breadcrumbs.map((crumb, index) => (
        <link
          key={index}
          rel="up"
          href={`${
            typeof window !== "undefined" ? window.location.origin : ""
          }${crumb.url}`}
        />
      ))}
      {/* Add specific category meta tags */}
      <meta property="category:name" content={safeValue(category.name)} />
      <meta property="category:level" content={String(category.level)} />
      {breadcrumbs.map((crumb, index) => (
        <meta
          key={`breadcrumb-${index}`}
          property="category:breadcrumb"
          content={safeValue(crumb.name)}
        />
      ))}
    </SEO>
  );
};

export default CategorySEO;
